import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check } from 'lucide-react';
import { cn } from '../../utils';

// D6-06: lightweight toast for transient functional feedback ("Copied to
// clipboard"). Deliberately minimal — a fixed bottom-center stack with
// auto-dismiss, announced politely to screen readers. Not a notification
// center; anything that needs to persist belongs in a banner, not here.

export interface ToastOptions {
  variant?: 'success' | 'error';
  durationMs?: number;
}

type ShowToastFn = (message: string, options?: ToastOptions) => void;

interface ToastItem {
  id: number;
  message: string;
  variant: 'success' | 'error';
  durationMs: number;
}

interface ToastContextValue {
  showToast: ShowToastFn;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION_MS = 2500;
const MAX_VISIBLE_TOASTS = 3;

// No-op fallback so leaf components (e.g. MessageBubble) stay renderable in
// isolation — unit tests and storybook-style harnesses don't need a provider.
const noopToast: ToastContextValue = { showToast: () => {} };

function ToastPill({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  useEffect(() => {
    const timeoutId = window.setTimeout(() => onDismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(timeoutId);
  }, [onDismiss, toast.durationMs, toast.id]);

  return (
    <div
      className={cn(
        'pointer-events-auto flex items-center gap-2 rounded-full border bg-card/95 py-2 pl-3 pr-4 text-sm text-foreground shadow-lg shadow-black/25 backdrop-blur-sm',
        toast.variant === 'error' ? 'border-red-500/30' : 'border-border',
      )}
      data-testid="toast"
    >
      {toast.variant === 'error' ? (
        <AlertCircle aria-hidden="true" className="h-4 w-4 flex-shrink-0 text-red-300" />
      ) : (
        <Check aria-hidden="true" className="h-4 w-4 flex-shrink-0 text-emerald-400" />
      )}
      <span className="min-w-0 break-words">{toast.message}</span>
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextIdRef = useRef(1);

  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback<ShowToastFn>((message, options) => {
    const toast: ToastItem = {
      id: nextIdRef.current++,
      message,
      variant: options?.variant ?? 'success',
      durationMs: options?.durationMs ?? DEFAULT_DURATION_MS,
    };
    setToasts((current) => [...current, toast].slice(-MAX_VISIBLE_TOASTS));
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-6 z-[70] flex flex-col items-center gap-2 px-4 pb-[env(safe-area-inset-bottom)]"
      >
        {toasts.map((toast) => (
          <ToastPill key={toast.id} toast={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  return useContext(ToastContext) ?? noopToast;
}
